import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { logQuietError } from "@/lib/quiet-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Users,
  Sparkles,
  Copy,
  Trophy,
  Send,
  Loader2,
  Gift,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { Skeleton, SkeletonRow } from "@/components/Skeleton";
import { toast } from "sonner";

export default function ReferAndEarn() {
  const [code, setCode] = useState(null);
  const [history, setHistory] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, h, lb] = await Promise.all([
          api.get("/referrals/me"),
          api.get("/referrals/history"),
          api.get("/referrals/leaderboard"),
        ]);
        if (!alive) return;
        setCode(c.data);
        setHistory(h.data);
        setLeaderboard(lb.data);
      } catch (e) {
        logQuietError("referral.load", e);
        if (alive) toast.error("Could not load referral data");
      }
    })();
    return () => { alive = false; };
  }, []);

  const copyLink = async () => {
    if (!code) return;
    const url = `${window.location.origin}${code.share_url_path}`;
    try { await navigator.clipboard.writeText(url); toast.success("Referral link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const copyText = async () => {
    if (!code) return;
    const url = `${window.location.origin}${code.share_url_path}`;
    try { await navigator.clipboard.writeText(`${code.share_text}\n${url}`); toast.success("Invite message copied"); }
    catch { toast.error("Copy failed"); }
  };

  const shareNative = async () => {
    if (!code) return;
    const url = `${window.location.origin}${code.share_url_path}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Join me on TeeBee", text: code.share_text, url });
      } catch (e) {
        // User-cancelled share is the common case; log quietly for prod observability.
        logQuietError("referral.share", e);
      }
    } else {
      copyLink();
    }
  };

  if (!code) {
    return (
      <Layout title="Refer & Earn">
        <Skeleton className="h-40 max-w-3xl" />
      </Layout>
    );
  }

  return (
    <Layout title="Refer & Earn">
      <div className="mb-6">
        <span className="tb-chip-mint inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Gift className="h-3 w-3" /> Refer & Earn
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          Invite teachers · Earn coins together
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
          When a teacher signs up using your code and creates their first AI generation, you both get 50 TeeBee Coins. No limit.
        </p>
      </div>

      {/* ===== Code hero ===== */}
      <div className="tb-card-feature relative overflow-hidden mb-6" data-testid="referral-hero">
        <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none" />
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-5 items-end">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Your code</p>
            <p className="mt-2 font-mono text-3xl sm:text-4xl tracking-widest font-medium" data-testid="referral-code">
              {code.code}
            </p>
            <p className="mt-3 text-xs text-muted-foreground break-all" data-testid="referral-link">
              {window.location.origin}{code.share_url_path}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button className="rounded-full" onClick={shareNative} data-testid="referral-share">
              <Send className="h-3.5 w-3.5 mr-1.5" /> Share
            </Button>
            <Button variant="outline" className="rounded-full" onClick={copyLink} data-testid="referral-copy-link">
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
            </Button>
            <Button variant="outline" className="rounded-full" onClick={copyText} data-testid="referral-copy-text">
              Copy invite text
            </Button>
          </div>
        </div>
        <div className="relative mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Invites sent"  value={code.invites_sent} icon={Send} />
          <Stat label="Pending"       value={code.pending}      icon={Clock} />
          <Stat label="Successful"    value={code.successful}   icon={CheckCircle2} chip="tb-chip-mint" />
          <Stat label="Coins earned"  value={code.coins_earned} icon={Sparkles}     chip="tb-chip-butter" />
        </div>
      </div>

      {/* ===== History + Leaderboard ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* History */}
        <div className="lg:col-span-7 tb-card p-5" data-testid="referral-history">
          <h3 className="font-display text-lg font-medium tracking-tight">Your invitations</h3>
          {history === null ? (
            <div className="mt-4 space-y-2"><SkeletonRow /><SkeletonRow /></div>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No invitations yet. Share your code with a teacher friend!</p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {history.map((h) => (
                <li key={h.referral_id} className="py-3 flex items-center gap-3">
                  <span className={`inline-grid place-items-center h-9 w-9 rounded-xl ${h.status === "completed" ? "tb-chip-mint" : "tb-chip-butter"}`}>
                    {h.status === "completed" ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{h.referee_name || h.referee_email || "Friend"}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {h.status === "completed" ? `Completed · ${h.coins_credited} coins` : "Pending — they need to create their first lesson"}
                    </p>
                  </div>
                  <p className="text-[11px] text-muted-foreground shrink-0">{new Date(h.created_at).toLocaleDateString()}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Leaderboard */}
        <div className="lg:col-span-5 tb-card p-5" data-testid="referral-leaderboard">
          <h3 className="font-display text-lg font-medium tracking-tight inline-flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-500" /> Top inviters
          </h3>
          {leaderboard === null ? (
            <div className="mt-4 space-y-2"><SkeletonRow /><SkeletonRow /></div>
          ) : leaderboard.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Be the first on the leaderboard.</p>
          ) : (
            <ol className="mt-3 space-y-2">
              {leaderboard.map((row) => (
                <li key={row.rank} className="flex items-center gap-3 p-2 rounded-xl hover:bg-muted/40">
                  <span className={`inline-grid place-items-center h-8 w-8 rounded-full font-mono text-xs font-bold ${row.rank === 1 ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200" : "bg-muted text-muted-foreground"}`}>
                    {row.rank}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{row.display_name}</p>
                    {row.badge && <p className="text-[10px] text-primary uppercase tracking-[0.18em] font-semibold">{row.badge}</p>}
                  </div>
                  <p className="font-mono text-xs text-muted-foreground shrink-0">{row.referrals} ref · +{row.coins_earned}</p>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Stat({ label, value, icon: Icon, chip = "tb-chip-sky" }) {
  return (
    <div className="rounded-2xl bg-card/70 backdrop-blur p-4 border border-border/60">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">
        <span className={`inline-grid place-items-center h-5 w-5 rounded-md ${chip}`}>
          <Icon className="h-3 w-3" />
        </span>
        {label}
      </div>
      <p className="mt-1.5 font-display text-2xl tracking-tight font-medium">{(value || 0).toLocaleString()}</p>
    </div>
  );
}
